import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../redux/store'
import { ErrorLogger } from '../../../../utils/Logger';
import { ActionCard } from '../../../../Models/ActionCard'
import { CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material'
import { CheckCircle, Schedule, Report, LockOutlined, Block } from '@mui/icons-material'
import { ActionCardAPIHandler } from '../../../../services/NetworkClient/BackendAPIs/ActionCardAPIHandler'
import GGDialogueBox, { dialogueType } from '../../../UtilityComponents/GGDialogueBox/GGDialogueBox'
import GGLoaderButton from '../../../CommonComponents/GGLoaderButton/GGLoaderButton'
import { useNavigate } from 'react-router-dom'
import { PaiseToRupeesConversion } from '../../../../utils/MoneyConversionUtils'
import { SessionManager } from '../../../../services/SessionManager/SessionManager'
import { getDateFromTimeStamp } from '../../../../utils/DateAndTimeUtils'
import { setCardMap } from '../../../../redux/actionCard/paymentCard/paymentCardsByChannelSlice'
import CustomMessageDeliveryHandler from '../../../../services/ChatManager/CustomMessageDeliveryHandler'
import { OrderProposalActionType } from '../GGCustomMessageTypes'

interface PaymentCardProps {
    actionCardId: string;
    channelId: string;
}

const PaymentCard = ({ actionCardId, channelId }: PaymentCardProps) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const getResponsiveStyles = () => ({
        PaymentCard: {
            border: "1px solid gray",
            borderRadius: isMobile ? "15px" : "20px",
            padding: isMobile ? "0.75rem" : "1rem",
            width: "100%",
            maxWidth: "350px",
            margin: isMobile ? "1.25rem 0px" : "1.5rem 0px",
            minHeight: '200px'
        },
        Bold: {
            fontWeight: "600",
            fontSize: isMobile ? "1rem" : "1.2rem",
        },
        SemiBold: {
            fontWeight: "600",
            fontSize: isMobile ? "0.9rem" : "1rem",
        },
        Amount: {
            fontSize: isMobile ? "1.5rem" : "2rem",
            fontWeight: "600",
            marginBottom: isMobile ? "8px" : "10px",
        },
        Delivery: {
            marginBottom: isMobile ? "8px" : "10px",
            fontSize: isMobile ? "1rem" : "1.2rem",
        },
        Revisions: {
            marginBottom: isMobile ? "8px" : "10px",
            fontSize: isMobile ? "1rem" : "1.2rem",
        },
        Deliverables: {
            marginBottom: isMobile ? "8px" : "10px",
            fontSize: isMobile ? "0.9rem" : "1rem",
        },
        ButtonBox: {
            display: 'flex' as const,
            justifyContent: 'center' as const,
            marginTop: isMobile ? '0.75rem' : '1rem',
            marginBottom: isMobile ? '3px' : '5px',
            flexWrap: 'wrap' as const,
            gap: isMobile ? '6px' : '8px',
        },
        PaymentButton: {
            display: "flex" as const,
            justifyContent: "center" as const,
            alignItems: "center" as const,
            fontSize: isMobile ? '11px' : '12px',
            backgroundColor: "#006CFF",
            color: "white",
            outline: "none",
            border: "none",
            cursor: "pointer" as const,
            padding: isMobile ? ".3rem 0.8rem" : ".4rem 1rem",
            minWidth: isMobile ? "120px" : "150px",
            borderRadius: isMobile ? "80px" : "100px"
        },
        PaymentButtonDecline: {
            border: '1px solid grey',
            backgroundColor: "#FFFFFF",
            color: "grey",
            marginRight: isMobile ? "0px" : "10px",
            minWidth: isMobile ? "80px" : "100px",
            fontSize: isMobile ? "11px" : "12px"
        }
    });

    const PaymentCardStyle = getResponsiveStyles();

    const channelCardMap = useSelector((state: RootState) => state.channelPaymentCardMap.cardMap)
    const initialStateActionCard = channelCardMap[actionCardId]
    const isCurrentUserBuyer = initialStateActionCard?.metaData.buyerId === SessionManager.shared.buyer?.id

    const [actionCard, setActionCard] = useState<ActionCard>(initialStateActionCard)
    const [showDeclineActionCardHurdle, setShowDeclineActionCardHurdle] = useState(false);
    const [loading, ] = useState(false);

    const fetchActionCardsForChannel =  async () => {
      const cardsList = await ActionCardAPIHandler.shared.getActionCardsByChannelId(channelId);
      if (cardsList) {
        dispatch(setCardMap(cardsList));
      }
    }

    useEffect(() => {
        setActionCard(channelCardMap[actionCardId])
    // eslint-disable-next-line
    }, [channelCardMap])

    useEffect(() => {
        if (!initialStateActionCard) {
            fetchActionCardsForChannel()
        }
    // eslint-disable-next-line
    }, [initialStateActionCard])

    const actionCardAcceptedLabel = (
        <Typography color={'success'} fontWeight={"700"} fontSize={"20px"} sx={{ color: "green", display: "flex", alignItems: "center", padding: "0px 10px", gap: "10px" }}>
            <CheckCircle /> Accepted
        </Typography>
    )

    const actionCardRejectedLabel = (
        <Typography fontWeight={"700"} fontSize={"20px"} sx={{ color: "red", display: "flex", alignItems: "center", padding: "0px 10px", gap: "10px" }}>
            <Report /> Rejected
        </Typography>
    )

    const actionCardUnknownLabel = (
        <Typography fontWeight={"700"} fontSize={"20px"} sx={{ color: "red", display: "flex", alignItems: "center", padding: "0px 10px", gap: "10px" }}>
            ℹ️ Unknown Status
        </Typography>
    )

    const actionCardExpiredLabel = (
        <Typography fontWeight={"700"} fontSize={"20px"} sx={{ color: "grey", display: "flex", alignItems: "center", padding: "10px 10px", gap: "10px" }}>
            <Schedule /> Expired
        </Typography>
    );

    const sendOrderProposalActionTakenCustomMessage = () => {
        try {
            const customMessageDeliveryHandler = new CustomMessageDeliveryHandler(channelId);
            let actionType = isCurrentUserBuyer ? OrderProposalActionType.rejected_by_buyer : OrderProposalActionType.revoked_by_seller
            customMessageDeliveryHandler.sendOrderProposalActionTakenCustomMessage(actionType, {
                buyerId: actionCard.metaData.buyerId,
                sellerId: actionCard.metaData.sellerId
            })
        } catch (error) {
            ErrorLogger.log(error, 'PaymentCard.sendOrderProposalActionTakenCustomMessage');
        }
    }

    const handleDeclineActionCard = async () => {

        try {
            await ActionCardAPIHandler.shared.updateActionCard(actionCard.id, "rejected");
            sendOrderProposalActionTakenCustomMessage()
            const newCardInfo = { ...actionCard, status: "rejected" }
            setActionCard(newCardInfo);
        } catch (error) {
            ErrorLogger.log(error, 'PaymentCard.declineActionCard');
        } finally {
            setShowDeclineActionCardHurdle(false)
        }
    }

    const PaymentCardForBuyer = (actionCardData: any) => {

        const renderActionButton = () => {
            const status = actionCardData.status;  // get status once

            if (status === 'accepted') {
                return actionCardAcceptedLabel
            } else if (status === 'rejected') {
                return actionCardRejectedLabel
            } else if (status === 'expired') {
                return actionCardExpiredLabel
            } else if (status === 'pending' && new Date(actionCardData.validTill) >= new Date()) {
                return (
                    <>
                        <button onClick={() => setShowDeclineActionCardHurdle(true)} style={{ ...PaymentCardStyle.PaymentButton, ...PaymentCardStyle.PaymentButtonDecline }}>Decline</button>
                        <GGLoaderButton onClick={routeToOrderConfirmationPage} isLoading={loading} style={PaymentCardStyle.PaymentButton} colorHex='#bcbcbc'> <LockOutlined /> &nbsp;Place Order</GGLoaderButton>
                    </>
                )
            } else if (new Date(actionCardData.validTill) < new Date()) {
                return actionCardExpiredLabel
            } else {
                return actionCardUnknownLabel
            }
        };

        const routeToOrderConfirmationPage = async () => {
            navigate("/create-order", { state: { actionCardData } });
        };

        return (
            <div className='Actioncard Paymentcard' style={PaymentCardStyle.PaymentCard}>
                <div className='amount' style={PaymentCardStyle.Amount}>Rs. {PaiseToRupeesConversion(actionCardData.orderDetails.amount)}</div>
                <div className='delivery' style={PaymentCardStyle.Delivery}>Delivery: <span style={PaymentCardStyle.Bold}>{getDateFromTimeStamp(Date.now() + actionCardData.orderDetails.deliveryDurationEpoch)}</span></div>
                <div className='revisions' style={PaymentCardStyle.Revisions}>Free Revisions: <span style={PaymentCardStyle.Bold}>{actionCardData.orderDetails.noOfRevisions}</span></div>
                <div className='revision charge' style={PaymentCardStyle.Revisions}>Extra Revision Charges: <span style={PaymentCardStyle.Bold}>{PaiseToRupeesConversion(actionCardData.orderDetails.revisionCharge)}</span></div>
                <div className='deliverables' style={PaymentCardStyle.Deliverables}>
                    <span style={PaymentCardStyle.SemiBold}>Deliverables: </span>
                    <br />
                    {actionCardData.orderDetails.deliverables && actionCardData.orderDetails.deliverables.map((item: string, index: number) => <span key={index}>{item}, </span>)}
                </div>
                <div className='buttonBox' style={PaymentCardStyle.ButtonBox}>
                    {renderActionButton()}
                </div>
            </div>
        )
    }

    const PaymentCardForSeller = (actionCardData: any) => {

        const renderActionButton = () => {
            const status = actionCardData.status;  // get status once


            if (status === 'accepted') {
                return actionCardAcceptedLabel
            } else if (status === 'rejected') {
                return actionCardRejectedLabel
            } else if (status === 'expired') {
                return actionCardExpiredLabel
            } else if (status === 'pending' && new Date(actionCardData.validTill) >= new Date()) {
                return (
                    <button onClick={() => setShowDeclineActionCardHurdle(true)} style={{ ...PaymentCardStyle.PaymentButton, ...PaymentCardStyle.PaymentButtonDecline }}><Block /> &nbsp;Revoke Order</button>
                )
            } else if (new Date(actionCardData.validTill) < new Date()) {
                return actionCardExpiredLabel
            } else {
                return actionCardUnknownLabel
            }
        };

        return (
            <div className='Actioncard Paymentcard' style={PaymentCardStyle.PaymentCard}>
                <div className='amount' style={PaymentCardStyle.Amount}>Rs. {PaiseToRupeesConversion(actionCardData.orderDetails.amount)}</div>
                <div className='delievery' style={PaymentCardStyle.Delivery}>Delivery: <span style={PaymentCardStyle.Bold}>{getDateFromTimeStamp(Date.now() + actionCardData.orderDetails.deliveryDurationEpoch)}</span></div>
                <div className='revisions' style={PaymentCardStyle.Revisions}>Free Revisions: <span style={PaymentCardStyle.Bold}>{actionCardData.orderDetails.noOfRevisions}</span></div>
                <div className='revision charge' style={PaymentCardStyle.Revisions}>Extra Revision Charges: <span style={PaymentCardStyle.Bold}>{PaiseToRupeesConversion(actionCardData.orderDetails.revisionCharge)}</span></div>
                <div className='deliverables' style={PaymentCardStyle.Deliverables}>
                    <span style={PaymentCardStyle.SemiBold}>Deliverables: </span>
                    <br />
                    {actionCardData.orderDetails.deliverables && actionCardData.orderDetails.deliverables.map((item: string, index: number) => <span key={index}>{item}, </span>)}
                </div>
                <div className='buttonBox' style={PaymentCardStyle.ButtonBox}>
                    {renderActionButton()}
                </div>
            </div>
        )
    }
    if (!actionCard) return (
        <div className='Actioncard Paymentcard center' style={PaymentCardStyle.PaymentCard}>
            <CircularProgress size={34} thickness={5} />
        </div>
    )
    return (
        <div>
            <GGDialogueBox
                type={dialogueType.Alert}
                isOpen={showDeclineActionCardHurdle}
                message={isCurrentUserBuyer ? "Are you sure you want to decline this order proposal? You will have to ask the freelancer to generate another order proposal." : "Are you sure you want to revoke this proposal? You will have to generate another proposal for your client."}
                closeHandler={() => setShowDeclineActionCardHurdle(false)}
                successHandler={handleDeclineActionCard}
            />
            {isCurrentUserBuyer ? PaymentCardForBuyer(actionCard) : PaymentCardForSeller(actionCard)}
        </div>
    )
}

export default PaymentCard